/**
 * L'assemblaggio.
 *
 * Due schermate, che sono esattamente il perimetro di questa sessione: la **guida TV con
 * il banco di misura** (fase 1) e l'**accesso** (l'impalcatura che la fase 3 riusa).
 *
 * L'autologin è quello di 02-ACCESSO §4.2, con le sue tre regole:
 *
 *  1. **un errore di rete non è un rifiuto** — non si butta fuori nessuno perché il Wi-Fi
 *     ha singhiozzato;
 *  2. **il motivo si mostra** — tornando all'accesso i campi sono già pieni;
 *  3. **le credenziali si cancellano solo uscendo**, o quando il server dice *no*.
 */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  thrownDetail,
  forgetsCredentials,
  windowFromStart,
  type Channel,
  type Credentials,
  type GatewayError,
  type ProgramStore,
  type TextLocale,
} from '@player/domain';
import { parseChannels } from '@player/gateway-http';
import { STORAGE_KEYS } from '@player/storage-web';
import {
  BenchScreen,
  I18nProvider,
  ToastProvider,
  Translator,
  resolveLocale,
  type Locale,
  type Messages,
} from '@player/ui';
import { APP_VERSION, loadFixtureChannels, loadFixtures, type Composition } from './composition.js';
import { loadMessages } from './messages.js';
import { LiveHost } from './LiveHost.js';
import { TvStage } from './TvStage.js';
import { usePhoneLayout } from './usePhoneLayout.js';
import { PairingHost } from './PairingHost.js';
import { CatalogCache, USAGE_TICK_MS } from './catalogCache.js';

/**
 * Scadenza e connessioni in uso, in una riga.
 *
 * La data si formatta con la lingua dell'apparecchio: una scadenza scritta `2027-03-14` costringe
 * chi è al telefono con l'assistenza a leggerla cifra per cifra.
 */
/*
  Scadenza e connessioni, in una riga: è la prima cosa che l'assistenza chiede al telefono.

  ## I campi sono **obbligatori**, e non è pignoleria

  Qui erano tutti opzionali (`expiresAt?`), e un tipo largo che accetta tutto non protegge da niente:
  questa funzione leggeva `expiresAt` mentre `parseLoginResult` produce **`expiresAtMs`** — un nome
  diverso e un tipo diverso. Il campo era sempre `undefined`, quindi la scadenza diceva sempre `—`,
  e TypeScript non aveva niente da obiettare perché mancare un campo opzionale è legittimo.

  Dichiarandoli obbligatori, lo stesso errore domani non compila. È la terza volta oggi che due parti
  si scambiano un dato con nomi diversi, ciascuna corretta da sola.
*/
interface LineFacts {
  readonly expiresAtMs: number | null;
  readonly activeConnections: number;
  readonly maxConnections: number;
}

function describeLine(line: LineFacts, messages: Messages): string {
  /*
    `null` non è un dato mancante: è **«la linea non scade»**, che è un fatto diverso.

    Prima si mostrava `—`, che si legge come «non lo so» — e su una linea senza scadenza era una
    informazione buona spacciata per un buco. La parola è tradotta, e si legge dai messaggi invece
    che dal traduttore perché qui siamo **sopra** `I18nProvider`: il componente che lo monta non può
    usarne il contesto.
  */
  const scadenza =
    line.expiresAtMs === null
      ? ((messages as Record<string, string>)['settings.subscription.unlimited'] ?? '—')
      : new Date(line.expiresAtMs).toLocaleDateString();

  return `${scadenza} · ${line.activeConnections}/${line.maxConnections}`;
}

/** La finestra della guida: 150 minuti, come il prototipo (03-SCHERMATE-TV §5). */
const GUIDE_MINUTES = 150;

type Screen =
  | { readonly kind: 'boot' }
  /*
    L'accoppiamento. Non porta più né un errore né dei campi da riempire: non c'è un modulo da
    ricompilare, c'è un codice da farsi leggere, e il motivo di un rifiuto lo dice il giro nuovo —
    che comincia da capo comunque.
  */
  | { readonly kind: 'pairing'; readonly notice: GatewayError | null }
  | { readonly kind: 'bench'; readonly channels: ReadonlyArray<Channel>; readonly programs: ProgramStore }
  /*
    La diretta porta con sé le credenziali, e non è una svista: l'indirizzo di un canale si compone
    con esse (`liveStreamUrl`), quindi o stanno qui o la schermata dovrebbe rileggerle dall'archivio
    a ogni zapping — una lettura cifrata ogni 400 ms su un apparecchio da trenta euro.
  */
  | {
      readonly kind: 'live';
      readonly channels: ReadonlyArray<Channel>;
      readonly credentials: Credentials;
      /**
       * Lo stato della linea, già in forma leggibile.
       *
       * Si compone **all'ingresso**, dai dati dell'accesso: le impostazioni lo mostrano in sola
       * lettura perché è la prima cosa che l'assistenza chiede al telefono, e chiederlo di nuovo al
       * gateway ogni volta che si apre quella schermata sarebbe una chiamata per un dato che non
       * cambia mentre si guarda la TV.
       */
      /*
        I dati della linea **grezzi**, non la frase già composta.

        La frase contiene «Illimitato» quando la linea non scade, e quella parola è tradotta: va
        composta dove i messaggi ci sono di sicuro. Qui si entra al login, e in quel momento
        `messages` può essere ancora `null` — comporla lì darebbe la parola inglese a tutti, o un
        guasto a seconda di come la si legge.
      */
      readonly line: LineFacts;
      /** L'ultimo canale guardato su questo apparecchio, letto **prima** di disegnare. */
      readonly lastChannelId: string | null;
    };

/**
 * I programmi arrivati finora, per canale.
 *
 * Una mappa e non un `ProgramStore` perché lo store è **immutabile per costruzione** — è quello che
 * gli permette di tenere dodicimila righe in array tipizzati invece che in oggetti. Quindi si
 * accumula qui, e lo store si ricostruisce quando arrivano dati nuovi: raro, perché si chiede solo
 * quando il dito si ferma.
 */
export interface AppProps {
  readonly composition: Composition;
}

export function App({ composition }: AppProps): ReactNode {
  /*
    Quale forma ha lo schermo. Serve qui per decidere se avvolgere tutto nel palco del
    televisore: la domanda è la stessa che le schermate si fanno più in basso, e porla una volta
    sola evita che la cornice e il contenuto possano dare risposte diverse.
  */
  const phone = usePhoneLayout();
  /*
    Il catalogo sull'apparecchio, costruito **una volta** per tutta la vita dell'app.

    Costruirlo a ogni disegno darebbe un magazzino nuovo che non sa quante ore d'uso sono passate, e
    quindi ricontrollerebbe sempre: esattamente il carico che esiste per togliere.
  */
  const cache = useMemo(
    () => new CatalogCache(composition.storage, composition.createGateway()),
    [composition],
  );
  /*
    La lingua scelta a mano, letta dall'archivio locale **prima** di caricare i messaggi.

    `null` significa «non l'ho ancora letta», e non «come il sistema»: sono due cose diverse e
    confonderle darebbe un lampo di lingua sbagliata a ogni avvio — la schermata si disegnerebbe in
    tedesco perché il televisore è tedesco, e mezzo secondo dopo passerebbe all'italiano scelto.
    Qui si aspetta la lettura, che è una riga di `localStorage`.
  */
  const [chosenLocale, setChosenLocale] = useState<TextLocale | 'auto' | null>(null);
  useEffect(() => {
    void cache.settings().then((stored) => setChosenLocale(stored.locale));
  }, [cache]);

  /**
   * La lingua effettiva.
   *
   * `'auto'` **segue** il sistema, e non è lo stesso che aver scelto quella lingua: se qualcuno
   * cambia la lingua del televisore, con `'auto'` l'app lo segue al riavvio, con una scelta esplicita
   * no. È la ragione per cui `'auto'` è un valore e non l'assenza di un valore.
   */
  const locale: Locale = useMemo(
    () =>
      chosenLocale === null || chosenLocale === 'auto'
        ? resolveLocale(navigator.languages ?? [navigator.language])
        : chosenLocale,
    [chosenLocale],
  );
  const [messages, setMessages] = useState<Messages | null>(null);
  const [screen, setScreen] = useState<Screen>({ kind: 'boot' });
  const [busy, setBusy] = useState(false);
  const [bootError, setBootError] = useState<string | null>(null);

  useEffect(() => {
    // Si aspetta di sapere quale lingua è stata scelta: caricare prima quella del sistema e poi
    // quella scelta vorrebbe dire due caricamenti e un lampo di lingua sbagliata.
    if (chosenLocale === null) return;
    loadMessages(locale).then(setMessages, (error: unknown) => setBootError(String(error)));
  }, [locale, chosenLocale]);

  // ── Il banco di misura della fase 1 ────────────────────────────────────────
  //
  // «Niente autenticazione, niente disegno rifinito» (11-FASI, fase 1): la prova che
  // decide gira sul catalogo finto e non ha bisogno di un gateway. Ci si arriva con
  // `#/bench`, e l'accesso resta la schermata di partenza perché è quella della fase 3.
  const openBench = useCallback(async () => {
    setBusy(true);
    setBootError(null);
    try {
      const raw = await loadFixtureChannels();
      const channels = parseChannels(raw);
      const programs = await loadFixtures(channels.map((channel) => channel.id));
      setScreen({ kind: 'bench', channels, programs });
    } catch (error) {
      // Nessun ripiego silenzioso: se il catalogo non c'è si dice, non si mostra una
      // griglia vuota che sembra un catalogo senza canali.
      setBootError(
        error instanceof Error
          ? `${error.message} — manca il catalogo finto? Lancia: npm run fixtures`
          : String(error),
      );
    } finally {
      setBusy(false);
    }
  }, []);

  /**
   * La diretta: si entra qui dopo l'accesso, non più sul banco di misura.
   *
   * I canali si chiedono **una volta**, con l'`ETag` che l'adattatore gestisce già: su dodicimila
   * canali riscaricare l'elenco a ogni ingresso è la differenza fra un'app che si apre e una che
   * fa aspettare. Fino a che non arrivano si mostra un'attesa con le parole, mai una schermata
   * ferma — un cambio di schermata lento senza niente sopra sembra un'app bloccata.
   */
  const openLive = useCallback(
    async (credentials: Credentials) => {
      setBusy(true);
      setBootError(null);
      try {
        const gateway = composition.createGateway();
        await cache.load();
        /*
          All'accesso si ricontrolla, ma con l'`ETag` archiviato: il server risponde `304` e non
          manda niente. Prima si chiedeva il listino intero — 4,91 MB su un catalogo vero — a **ogni**
          apertura, perché l'`ETag` viveva in memoria e moriva con la sessione.
        */
        const [stato, catalog, lastChannelId] = await Promise.all([
          gateway.me(credentials),
          cache.channels(credentials, true),
          // Si legge **qui**, insieme al catalogo: passandolo già risolto, la diretta apre subito il
          // canale giusto invece di aprirne uno e saltare — due sessioni per un'accensione.
          cache.lastChannel(),
        ]);
        setScreen({
          kind: 'live',
          channels: catalog.channels,
          credentials,
          line: stato.line,
          lastChannelId,
        });
      } catch (error) {
        const detail =
          thrownDetail(error, Date.now());
        setScreen({ kind: 'pairing', notice: detail });
      } finally {
        setBusy(false);
      }
    },
    [composition, cache],
  );

  /*
    Il battito che conta le **ore d'uso**.

    Non è l'orologio: un televisore in standby per tre giorni non deve svegliarsi con tutto scaduto e
    riscaricare il catalogo. Il dominio scarta i passi troppo lunghi, quindi uno standby non si
    somma.
  */
  useEffect(() => {
    let last = Date.now();
    const timer = setInterval(() => {
      const now = Date.now();
      void cache.tick(now - last);
      last = now;
    }, USAGE_TICK_MS);
    return () => clearInterval(timer);
  }, [cache]);

  // ── Autologin ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (window.location.hash === '#/bench') {
      void openBench();
      return;
    }
    let cancelled = false;
    void (async () => {
      const stored = await composition.storage.readSecure(STORAGE_KEYS.credentials);
      if (cancelled) return;
      if (!stored) {
        setScreen({ kind: 'pairing', notice: null });
        return;
      }
      const credentials = JSON.parse(stored) as Credentials;
      try {
        const result = await composition.createGateway().login(credentials);
        composition.clock.sync(result.serverNowMs);
        if (!cancelled) await openLive(credentials);
      } catch (error) {
        if (cancelled) return;
        const detail =
          thrownDetail(error, Date.now());
        /*
          Due regole, e la seconda è cambiata con l'accesso per codice.

          1. **Un errore di rete non è un rifiuto**: se il gateway non risponde non si butta fuori
             nessuno perché il Wi-Fi ha singhiozzato.
          2. **Uno stato reversibile non cancella niente**: linea scaduta, sospesa o disattivata
             lasciano le credenziali dov'erano. Prima si cancellavano, e andava bene finché si
             rientrava riscrivendo un token; adesso rientrare richiede un rivenditore raggiungibile
             che scansioni, quindi cancellare per una scadenza significa mandare un cliente in
             assistenza per una cosa che si sarebbe risolta da sé al rinnovo.
        */
        if (forgetsCredentials(detail.code)) {
          await composition.storage.removeSecure(STORAGE_KEYS.credentials);
        }
        setScreen({ kind: 'pairing', notice: detail });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [composition, openLive]);

  /**
   * Le credenziali sono arrivate dall'accoppiamento: si provano, si conservano, si entra.
   *
   * Si prova **prima** di conservare. Un deposito sbagliato — la linea di un altro cliente, un
   * gateway spento — resterebbe altrimenti sull'apparecchio, e a ogni accensione riporterebbe qui
   * con lo stesso errore senza che nessuno capisca di doverlo ri-attivare.
   */
  const enterWith = useCallback(
    async (credentials: Credentials) => {
      setBusy(true);
      try {
        const result = await composition.createGateway().login(credentials);
        composition.clock.sync(result.serverNowMs);
        await composition.storage.writeSecure(STORAGE_KEYS.credentials, JSON.stringify(credentials));
        await openLive(credentials);
      } catch (error) {
        const detail =
          thrownDetail(error, Date.now());
        setScreen({ kind: 'pairing', notice: detail });
      } finally {
        setBusy(false);
      }
    },
    [composition, openLive],
  );

  if (bootError) {
    // Questo messaggio è volutamente fuori dall'i18n: è un guasto di avviamento
    // dell'applicazione, non un testo di prodotto, e lo legge chi sviluppa.
    return <pre style={errorStyle}>{bootError}</pre>;
  }
  if (!messages) return <div style={errorStyle} />;

  return (
    <I18nProvider locale={locale} messages={messages}>
      {/*
        Il toast sta **sopra tutte le schermate** e dentro l'i18n: i riscontri arrivano già tradotti
        da chi li mostra, e devono sopravvivere al cambio di schermata — si aggiunge un preferito dalla
        guida e si torna al lettore, e la parola deve restare quei due secondi.
      */}
      <ToastProvider>
      {/*
        Il palco del televisore avvolge **tutte** le schermate, non solo quelle dentro il menu.

        La prima a rompersi era l'attivazione, che sta fuori: è la schermata che un apparecchio nuovo
        vede per prima, e su Android TV il QR e il codice finivano oltre il bordo destro. Avvolgere
        solo la parte dopo l'accesso avrebbe corretto tutto tranne il punto da cui non si passa.

        Sul telefono **non** si scala: là le schermate sono costruite sulla larghezza vera, e
        rimpicciolire una tela da 1920 darebbe caratteri da sei punti.
      */}
      <Palco phone={phone}>
      {screen.kind === 'bench' ? (
        <BenchScreen
          focusPort={composition.focusPort}
          channels={screen.channels}
          programs={screen.programs}
          window={windowFromStart(guideWindowStart(), GUIDE_MINUTES)}
          serverNowMs={composition.clock.isSynced ? composition.clock.nowMs() : Date.now()}
          createPlayer={composition.createPlayer}
          platformLabel={composition.platformLabel}
          missingKeys={new Translator(locale, messages).missingKeys}
        />
      ) : screen.kind === 'live' ? (
        <LiveHost
          composition={composition}
          cache={cache}
          lastChannelId={screen.lastChannelId}
          channels={screen.channels}
          credentials={screen.credentials}
          guideStartMs={guideWindowStart()}
          guideMinutes={GUIDE_MINUTES}
          subscription={describeLine(screen.line, messages)}
          // La lingua scelta nelle impostazioni: la tiene questo componente, perché è lui a montare
          // `I18nProvider`. Cambiandola, i messaggi si ricaricano e la schermata si ridisegna.
          onLocaleChange={setChosenLocale}
          onExit={() => {
            void composition.storage.removeSecure(STORAGE_KEYS.credentials);
            // Uscendo si butta anche il catalogo: quello di una linea non deve restare leggibile a
            // quella dopo.
            void cache.clear();
            setScreen({ kind: 'pairing', notice: null });
          }}
          onCatalogRefreshed={() => {
            // Il magazzino è già aggiornato: si rilegge da lì, senza toccare la rete una seconda
            // volta. Chiedere di nuovo al gateway sarebbe la stessa risposta pagata due volte.
            void cache.channels(screen.credentials, false).then((catalog) => {
              setScreen((before) =>
                before.kind === 'live' ? { ...before, channels: catalog.channels } : before,
              );
            });
          }}
          onSendBackToLogin={(error) => {
            void composition.storage.removeSecure(STORAGE_KEYS.credentials);
            setScreen({ kind: 'pairing', notice: error });
          }}
        />
      ) : screen.kind === 'pairing' ? (
        <PairingHost
          focusPort={composition.focusPort}
          deviceId={composition.deviceId}
          appVersion={APP_VERSION}
          pairing={composition.pairing}
          elapsedMs={monotonicNow}
          notice={screen.notice}
          entering={busy}
          onPaired={(credentials) => void enterWith(credentials)}
        />
      ) : (
        <div style={errorStyle} />
      )}
      </Palco>
      </ToastProvider>
    </I18nProvider>
  );
}

/**
 * La finestra si apre alla mezz'ora tonda **precedente**, non all'istante corrente: così
 * il programma in onda si vede per intero invece di cominciare al bordo sinistro, ed è
 * anche il caso che mette alla prova il taglio con il `‹`.
 */
function guideWindowStart(): number {
  const halfHour = 30 * 60_000;
  return Math.floor(Date.now() / halfHour) * halfHour - halfHour;
}

/**
 * Il tempo monotono, per il conto alla rovescia dell'accoppiamento.
 *
 * Fuori dal componente perché deve essere **la stessa funzione a ogni disegno**: ricrearla
 * rimetterebbe in moto gli effetti che la ricevono, e quello dell'accoppiamento chiede un codice
 * nuovo ogni volta che riparte.
 */
function monotonicNow(): number {
  return performance.now();
}

const errorStyle = {
  height: '100%',
  margin: 0,
  padding: 40,
  background: '#0A0D13',
  color: '#FF6B61',
  font: '500 18px ui-monospace, Menlo, monospace',
  whiteSpace: 'pre-wrap' as const,
};


/**
 * Il palco, o niente.
 *
 * Su un televisore avvolge la tela 1920×1080 e la rimpicciolisce al riquadro vero; su un telefono
 * restituisce i figli così come sono. Un componente e non un ternario dentro il disegno perché il
 * ternario avrebbe cambiato l'albero di React fra le due forme: ruotando il telefono, ogni
 * schermata si sarebbe rimontata da zero — perdendo la posizione dell'elenco e, nel lettore, il
 * video.
 */
function Palco({ phone, children }: { readonly phone: boolean; readonly children: ReactNode }): ReactNode {
  return phone ? <>{children}</> : <TvStage>{children}</TvStage>;
}
